import React from 'react';
import { Navigate, matchPath } from 'react-router-dom';
import Layers from '@mui/icons-material/Layers';
import Speed from '@mui/icons-material/Speed';
import ScienceRounded from '@mui/icons-material/ScienceRounded';
import CalendarViewWeek from '@mui/icons-material/CalendarViewWeek';
import PaletteRounded from '@mui/icons-material/PaletteRounded';
import QrCodeScannerRounded from '@mui/icons-material/QrCodeScannerRounded';
import ShoppingCartRounded from '@mui/icons-material/ShoppingCartRounded';
import PrecisionManufacturingRounded from '@mui/icons-material/PrecisionManufacturingRounded';
import CompareArrowsRounded from '@mui/icons-material/CompareArrowsRounded';
import TimelineRounded from '@mui/icons-material/TimelineRounded';
import AltRouteRounded from '@mui/icons-material/AltRouteRounded';
import BrokenImageRounded from '@mui/icons-material/BrokenImageRounded';

import SequencesView from './SequencesView';
import StandardsView from './StandardsView';
import InkMaterialsView from './InkMaterialsView';
import RunReadinessView from './RunReadinessView';
import InkAssignmentView from './InkAssignmentView';
import InkComparisonView from './InkComparisonView';
import InkStickerView from './InkStickerView';
import InkTrolleyView from './InkTrolleyView';
import KitTrolleyPage from './inkTrolley/KitTrolleyPage';
import InkPressView from './InkPressView';
import PressScheduleGantt from './board/PressScheduleGantt';
import InkForecastView from './inkForecast/InkForecastView';
import InkOrderDocumentView from './inkForecast/InkOrderDocumentView';
import InkPurchaseOrderDocument from './InkPurchaseOrderDocument';
import ArtworkHealthView from './ArtworkHealthView';

export interface ProductionRoute {
  path: string;
  label: string;
  title: string;
  icon: React.ReactNode;
  element: React.ReactNode;
  /** Render a divider immediately above this item in the sidebar. */
  dividerBefore?: boolean;
  /**
   * Pin this item to the BOTTOM of the sidebar. Used for features we're not
   * actively pursuing (Speeds, Sequences) but may return to — parked below a
   * spacer, out of the primary ink workflow.
   */
  pinBottom?: boolean;
  /**
   * Routed but not shown in the sidebar. Used for legacy-path redirects, which
   * must keep resolving (bookmarks, links in other views) without adding noise
   * to the nav.
   */
  hidden?: boolean;
  /** Where the sidebar link points, when that differs from the route pattern
   *  (e.g. a tabbed shell routed at `/ink/:tab` but linked to its default tab). */
  navPath?: string;
  /** Keep the sidebar item lit on any child path, not just an exact match —
   *  needed by tabbed shells, whose URL changes as you move between tabs. */
  matchPrefix?: boolean;
}

// Week / Unscheduled / Alerts views temporarily removed from nav (2026-04-28)
// while we iterate on the forme-grouped Gantt. Components retained on disk;
// re-add to this array once the schedule UI is settled.
//
// Array order = sidebar order. The ink workflow is the primary block (Board,
// then a divider, then the four Ink views); the deprioritised views carry
// `pinBottom` and sink below a flex spacer (see SidebarNav) 2026-07-14.
export const productionRoutes: ProductionRoute[] = [
  // The forme-grouped Gantt board is now the planning solution; '/production'
  // redirects here (see App.tsx). The old TodayView "Schedule" page was retired
  // 2026-06-23 — component retained on disk but no longer routed or in nav.
  { path: '/production/board', label: 'Board', title: 'Press Schedule Board', icon: <CalendarViewWeek />, element: <PressScheduleGantt /> },

  // ---- Ink ------------------------------------------------------------------
  // Stock / Demand / Catalogue / Suppliers were four separate nav items, which
  // crowded the sidebar and obscured that they are one workflow. They are now
  // four TABS of a single "Ink" entry (InkMaterialsView), ordered by the question
  // each answers — operational first, master data last:
  //     Stock (what we have) → Demand (what we need) → Catalogue (what it is)
  //     → Suppliers (who from).
  // Assign, Sticker and Trolley stay separate on purpose: they are things you DO
  // on the floor, not questions you ask about materials.
  {
    // navPath is the PARENT, not the default tab: NavLink prefix-matches on its
    // own `to`, so pointing at /ink/balance would only light up on the balance tab.
    // /production/ink redirects to the default tab (below). Segment matching
    // means this never collides with /production/ink-assign.
    path: '/production/ink/:tab', navPath: '/production/ink', matchPrefix: true,
    label: 'Ink', title: 'Ink — Balance, Demand, Catalogue & Suppliers',
    icon: <ScienceRounded />, element: <InkMaterialsView />, dividerBefore: true,
  },
  // The long-horizon report: 12 months of ink demand off the Prism board
  // forecast. It sits beside Ink rather than inside it because it answers a
  // different question on a different clock — Ink is "what do we have and need
  // now", this is "what will we need, and what do I buy".
  {
    path: '/production/ink-forecast', label: 'Ink forecast',
    title: 'Ink Demand Forecast — 12-month horizon by ink code',
    icon: <TimelineRounded />, element: <InkForecastView />,
  },
  // The Drum-Buffer-Rope lens. Belongs directly under the Board because it is
  // the Board's consequence: every deadline on it is pulled off planned_start.
  //
  // Hidden from the nav for now — the lens reads real production data and is
  // correct, but the orchestration behind it (helpers/orchestrator.py) is only
  // driven by scripts/sandbox/advance_time.py, so what a planner would see here
  // is the deadlines without anything acting on them. Reachable by URL for
  // demos. Drop `hidden` when the ropes fire outside the sandbox.
  {
    path: '/production/readiness', label: 'Run readiness',
    title: 'Run Readiness — upstream deadlines pulled by the press schedule',
    icon: <AltRouteRounded />, hidden: true, element: <RunReadinessView />,
  },
  { path: '/production/ink-assign', label: 'Ink assign', title: 'Ink Assignment — Client Colour Library', icon: <PaletteRounded />, element: <InkAssignmentView /> },
  { path: '/production/ink-sticker', label: 'Ink sticker', title: 'Ink Sticker — Scan Job Bag to Print', icon: <QrCodeScannerRounded />, element: <InkStickerView /> },
  { path: '/production/ink-trolley', label: 'Ink trolley', title: 'Ink Trolley — Kit a Gang Run & Reconcile', icon: <ShoppingCartRounded />, element: <InkTrolleyView /> },
  // The kit flow is its own page, not a dialog: it is a shelf-walking, scan-
  // every-tub job done on a tablet, and a pick list never fitted in a modal.
  // Hidden from the nav — you always arrive from the trolley screen's button.
  {
    path: '/production/ink-trolley/kit', label: '', title: 'Kit a Trolley — Scan Tubs onto a Gang Run',
    icon: null, hidden: true, element: <KitTrolleyPage />,
  },
  // The press-side terminal (roadmap session 2): scan the job bag, then each tub
  // as it goes on the press. This is where a supplier batch gets tied to a job.
  { path: '/production/ink-press', label: 'Ink at press', title: 'Ink at Press — Scan Tubs onto the Job', icon: <PrecisionManufacturingRounded />, element: <InkPressView /> },
  // A what-if, not an operational screen: price one item's ECG build against its
  // spot build before pre-press commits to one. Sits with Ink because it runs on
  // the same coverage × mileage model, but it reads no job and writes nothing.
  { path: '/production/ink-compare', label: 'ECG vs PMS', title: 'ECG vs PMS — Ink Cost Comparison', icon: <CompareArrowsRounded />, element: <InkComparisonView /> },

  // Everything above reads separations off the Cloudflow artwork, so when the
  // nightly warm can't read a file every one of those screens is quietly wrong
  // for that item. This is the worklist of exactly those items (migration 138)
  // — sitting at the foot of the ink block because it is where you go when a
  // number up there looks wrong, not somewhere you start the day.
  {
    path: '/production/artwork-health', label: 'Artwork health',
    title: 'Artwork Health — warm failures, spec vs artwork & path overrides',
    icon: <BrokenImageRounded />, element: <ArtworkHealthView />,
  },

  // The buyer's printed output. Routed but NOT in the nav: it is reached from
  // the forecast's "Order document" button, which hands it the selected window
  // in the hash. On its own it is a document, not a destination.
  {
    path: '/production/ink-order-document', label: '', title: 'Ink Purchasing Summary',
    icon: null, hidden: true, element: <InkOrderDocumentView />,
  },

  // The printable F-093 purchase order. Routed but NOT in the nav: it is a
  // document, reached from the Print button on a PO, and it needs ?po=<id> to
  // mean anything. Browser print, not a server-side PDF — see the component.
  {
    path: '/production/ink-purchase-order-document', label: '', title: 'Purchase Order — F-093',
    icon: null, hidden: true, element: <InkPurchaseOrderDocument />,
  },

  // Legacy paths — routed, not shown. Bookmarks and any in-app links to the old
  // standalone screens must keep working.
  { path: '/production/ink', label: '', title: '', icon: null, hidden: true, element: <Navigate to="/production/ink/balance" replace /> },
  { path: '/production/ink-stock', label: '', title: '', icon: null, hidden: true, element: <Navigate to="/production/ink/balance" replace /> },
  { path: '/production/ink-order', label: '', title: '', icon: null, hidden: true, element: <Navigate to="/production/ink/balance" replace /> },
  { path: '/production/ink-catalogue', label: '', title: '', icon: null, hidden: true, element: <Navigate to="/production/ink/catalogue" replace /> },
  // --- parked below (not actively pursued; retained on disk) --------------------
  { path: '/production/speeds', label: 'Speeds', title: 'Speeds & Standards', icon: <Speed />, element: <StandardsView />, pinBottom: true },
  { path: '/production/sequences', label: 'Sequences', title: 'Separation Sequences', icon: <Layers />, element: <SequencesView />, pinBottom: true },
];

/**
 * The route backing a pathname — for the shell's header title.
 *
 * Must go through matchPath rather than `r.path === pathname`: a tabbed shell is
 * routed on a PATTERN (`/production/ink/:tab`), which no literal pathname will
 * ever string-equal, so the old comparison silently fell back to "Production".
 * Hidden routes are redirects and never own a title.
 */
export function findProductionRoute(pathname: string): ProductionRoute | undefined {
  return productionRoutes.find(
    (r) => !r.hidden && matchPath({ path: r.path, end: !r.matchPrefix }, pathname) !== null,
  );
}
